import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, UserX } from 'lucide-react';
import type { ApiResponse } from '@evi/shared';
import { api, getDisplayErrorMessage } from '@/lib/api';
import styles from './SettingsPage.module.css';

/**
 * Internal and test addresses (Warren, 28 October 2026: "a staff test list I control in Admin"). Super Admin only (Api:
 * routes/settings/lead-internal-addresses.ts); at most 200; every save audited. Saving marks the leads the list now
 * names and releases the ones it no longer names; evinitiative.com addresses and drawer choices are never changed.
 */
const KEY = ['lead-internal-addresses'];
interface Value { addresses: string[]; is_default: boolean }
interface Saved { value: Value; marked: number; unmarked: number }

export function LeadInternalAddressesSection() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api.get('settings/lead-internal-addresses').json<ApiResponse<{ value: Value }>>().then(r => r.data!.value), staleTime: 60_000 });
  const [draft, setDraft] = useState<string[] | null>(null), [adding, setAdding] = useState(''), [saving, setSaving] = useState(false);
  const [error, setError] = useState(''), [saved, setSaved] = useState<string | null>(null);
  const list = draft ?? q.data?.addresses ?? [];
  const dirty = draft !== null && draft.join('\n') !== (q.data?.addresses ?? []).join('\n');
  function add() {
    const email = adding.trim().toLowerCase();
    if (!email) return;
    if (!list.includes(email)) setDraft([...list, email]);
    setAdding(''); setSaved(null);
  }
  async function save() {
    setSaving(true); setError(''); setSaved(null);
    try {
      const r = await api.put('settings/lead-internal-addresses', { json: { addresses: list } }).json<ApiResponse<Saved>>();
      await client.invalidateQueries({ queryKey: KEY }); setDraft(null);
      setSaved(`Saved. ${r.data!.marked} lead${r.data!.marked === 1 ? '' : 's'} marked internal, ${r.data!.unmarked} released.`);
    } catch (err) { setError(await getDisplayErrorMessage(err, 'The list was not saved.')); } finally { setSaving(false); }
  }
  return <section className={styles.platformFeeSection}>
    <div className={styles.platformFeeHeader}>
      <div className={styles.headerIcon}><UserX size={16} /></div>
      <div>
        <h2 className={styles.platformFeeTitle}>Internal and test addresses</h2>
        <p className={styles.platformFeeHint}>Leads from these addresses and from any evinitiative.com address are internal: never scored, never in the
          digest or speed-to-lead, never sent a campaign.</p>
      </div>
    </div>
    {q.isLoading ? <p className={styles.platformFeeHint}><Loader2 size={13} className={styles.spin} /> Loading.</p>
      : q.isError ? <p className={styles.platformFeeHint} style={{ color: 'var(--color-err)' }}>Couldn&rsquo;t load the address list. Refresh to retry.</p>
        : <>
          <div className={styles.platformFeeField}>
            <span className={styles.platformFeeLabel}>Staff test addresses{q.data?.is_default && draft === null ? ' (the starting list, not saved yet)' : ''}</span>
            {list.length === 0 ? <p className={styles.platformFeeHint}>None. Only evinitiative.com addresses are internal.</p>
              : <ul className={styles.platformFeeHint}>{list.map(email => <li key={email}>{email}{' '}
                <button type="button" className={styles.platformFeeBtn} disabled={saving}
                  onClick={() => { setDraft(list.filter(x => x !== email)); setSaved(null); }}>Remove</button></li>)}</ul>}
          </div>
          <div className={styles.platformFeeField}>
            <label className={styles.platformFeeLabel} htmlFor="lead-internal-add">Add an address</label>
            <div className={styles.platformFeeInputRow}>
              <input id="lead-internal-add" className={styles.platformFeeInput} type="email" maxLength={254} placeholder="name@example.com"
                value={adding} onChange={e => setAdding(e.target.value)} disabled={saving}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
              <button type="button" className={styles.platformFeeBtn} disabled={saving || !adding.trim()} onClick={add}>Add</button>
            </div>
          </div>
          <div className={styles.platformFeeInputRow}>
            <button type="button" className={styles.platformFeeBtn} disabled={saving || (!dirty && !q.data?.is_default)} onClick={() => void save()}>
              {saving ? <><Loader2 size={13} className={styles.spin} /> Saving</> : saved && !dirty ? <><Check size={13} /> Saved</> : <>Save</>}
            </button>
          </div>
          {saved && <p className={styles.platformFeeHint} role="status">{saved}</p>}
          {error && <p className={styles.platformFeeHint} role="alert" style={{ color: 'var(--color-err)' }}>{error}</p>}
        </>}
  </section>;
}
